import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ContentGrid } from '../../../partials/ContentGrid'
import SummaryPicture from '../../../partials/ContentGrid/SummaryPicture'
import Accordion from '../../../partials/Accordion/Accordion'
import { AccordionItemData } from '../../../partials/Accordion/components/AccordionItem'
import SectionWithTag from '../../../components/SectionWithTag'
import { SwitchTransition, CSSTransition } from 'react-transition-group'
import FadeIn from '../../../components/Animation/FadeIn'
import { useIsMobile } from '../../../utils/helpers'
import { SetPaddingProps } from '../../../utils/PaddingContext'

const id = 'page.epp.useCases'
const data: AccordionItemData[] = [
    {
        id: `${id}.biomanufacturing`,
        slug: 'biomanufacturing',
    },
    {
        id: `${id}.diagnostics`,
        slug: 'diagnostics',
    },
    {
        id: `${id}.biocatalysts`,
        slug: 'biocatalysts',
    },
    {
        id: `${id}.beverages`,
        slug: 'beverages',
    },
]

const timeout = 7000

const UseCases = ({ setPadding }: SetPaddingProps) => {
    const [activeItem, setActiveItem] = useState(0)
    const isMobile = useIsMobile()

    useEffect(() => {
        let intervalID: NodeJS.Timeout
        if (!isMobile) {
            intervalID = setInterval(
                () => setActiveItem(activeItem < data.length - 1 ? activeItem + 1 : 0),
                timeout,
            )
        }

        return () => {
            clearInterval(intervalID)
        }
    }, [activeItem, isMobile])

    const leftRef = useRef<HTMLDivElement>(null)
    const rightRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        const calculatePadding = () => {
            requestAnimationFrame(() => {
                if (leftRef.current && rightRef.current && contentRef.current && !isMobile) {
                    const paddingLeft =
            leftRef.current.getBoundingClientRect().left -
            contentRef.current.getBoundingClientRect().left
                    const paddingRight =
            window.innerWidth -
            rightRef.current.getBoundingClientRect().right -
            contentRef.current.getBoundingClientRect().left
                    setPadding({
                        paddingLeft,
                        paddingRight: paddingRight === 0 ? paddingLeft : paddingRight,
                    })
                } else {
                    setPadding()
                }
            })
        }
        calculatePadding()
        window.addEventListener('resize', calculatePadding)
        return () => {
            window.removeEventListener('resize', calculatePadding)
        }
    }, [leftRef, rightRef, isMobile, contentRef, loaded, setPadding])
    return (
        <div className='relative border-t border-black/10'>
            <div className='pt-12 md:pt-30 md:pb-20 md:hidden'>
                <FadeIn>
                    <SectionWithTag tag={{ id: `${id}.tag` }} className='3xl:max-w-screen-2xl mx-auto'>
                        <div className='pt-9 md:pt-0 max-w-[350px] md:max-w-[700px]'>
                            <FormattedMessage id={id} tagName='h2' />
                        </div>
                    </SectionWithTag>
                </FadeIn>
            </div>
            <div className=''>
                <ContentGrid className='3xl:max-w-screen-2xl mx-auto'>
                    <div className='aspect-auto'>
                        <div className='pt-10 xl:pt-24 bg-white aspect-square pb-16 md:pb-20' ref={contentRef}>
                            <div className='hidden md:block pb-12 lg:pb-20'>
                                <FadeIn>
                                    <SectionWithTag
                                        tag={{ id: `${id}.tag` }}
                                        className='3xl:max-w-screen-2xl mx-auto'
                                    >
                                        <div className='pt-9 md:pt-0 max-w-[350px] md:max-w-[700px]'>
                                            <FormattedMessage id={id} tagName='h2' />
                                        </div>
                                    </SectionWithTag>
                                </FadeIn>
                            </div>
                            <div className='side-padding'>
                                <div className=''>
                                    <div
                                        ref={leftRef}
                                        className='flex items-center md:h-fit w-full max-w-[410px] mx-auto'
                                    >
                                        <Accordion
                                            data={data}
                                            setActiveItem={setActiveItem}
                                            activeItem={activeItem}
                                            timeout={timeout}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='aspect-auto'>
                        <div className='hidden md:block flex items-center self-center'>
                            <div className=' h-full p-[15%]' ref={rightRef}>
                                <SwitchTransition mode='out-in'>
                                    <CSSTransition
                                        key={activeItem}
                                        addEndListener={(node, done) => {
                                            node.addEventListener('transitionend', done, false)
                                        }}
                                        classNames='fade'
                                    >
                                        <SummaryPicture
                                            slug={data[activeItem].slug || data[activeItem].id}
                                            imageClassName='w-full'
                                            className='aspect-square'
                                            onLoad={() => setLoaded(true)}
                                        />
                                    </CSSTransition>
                                </SwitchTransition>
                            </div>
                        </div>
                    </div>
                </ContentGrid>
            </div>
        </div>
    )
}

export default UseCases
